import { useMemo } from 'react';
import qs from 'qs';
import { useLocation } from 'react-router';

import { useQuery } from '@tanstack/react-query';
import dayjs from '@tt/dates/dayjs';

import {
    useAccountStore,
    useManagementStore,
    useSettingStore,
    useUserState
} from '../../../../store';
import axios from '../../../../utilities/axios';
import { TRADEBOOK } from '../../../../utilities/functions';

/**
 * Check if viewAs mode is active (viewing another user's data)
 */
export const useIsViewAsActive = () => {
    const { viewAs } = useManagementStore((state) => state);

    return useMemo(
        () =>
            JSON.stringify(viewAs?.newUser?.token || null) !==
            JSON.stringify(viewAs?.mainUser?.token || null),
        [viewAs?.newUser?.token, viewAs?.mainUser?.token]
    );
};

/**
 * Get the appropriate auth token based on viewAs state
 */
export const useAuthToken = () => {
    const { getUser } = useUserState();
    const { viewAs } = useManagementStore((state) => state);
    const isViewAsActive = useIsViewAsActive();
    const user = getUser();

    return isViewAsActive ? viewAs.newUser.token : user?.token;
};

/**
 * Fetch tags for filtering
 */
export const useTags = () => {
    const token = useAuthToken();
    const { viewAs } = useManagementStore((state) => state);

    return useQuery({
        queryKey: ['tags', viewAs],
        queryFn: async () => {
            const response = await axios.get('crud/tag', {
                headers: { token }
            });
            return response.data;
        },
        onError: (error) => {
            if (error.response?.status === 404) {
                return [];
            }
            throw error;
        },
        initialData: []
    });
};

/**
 * Fetch all symbols that have been traded
 */
export const useSymbols = () => {
    const { pathname } = useLocation();
    const { allAccounts } = useAccountStore((state) => state);
    const { getUser } = useUserState();
    const authToken = useAuthToken();
    const user = getUser();
    const isUnassignedRoute = pathname.startsWith('/admin/u/');
    const token = isUnassignedRoute ? user?.token : authToken;
    const { filter } = useSettingStore((state) => state);
    const filteredAccounts = Array.isArray(filter?.accounts) ? [...filter.accounts].sort() : [];
    const fallbackUnassignedAccounts = isUnassignedRoute
        ? allAccounts
              .map((account) => account?.key)
              .filter(Boolean)
              .sort()
        : [];
    const selectedAccounts =
        filteredAccounts.length > 0 ? filteredAccounts : fallbackUnassignedAccounts;
    const accountScopeKey = selectedAccounts.join(',');

    const { data: allSymbolsTraded = [], isFetched } = useQuery({
        queryKey: ['allSymbolsTraded', token || null, accountScopeKey],
        queryFn: async () => {
            const response = await axios.get('crud/symbols', {
                headers: { token },
                skipViewAs: isUnassignedRoute,
                params: { accounts: selectedAccounts },
                paramsSerializer: (params) => {
                    const searchParams = new URLSearchParams();
                    const accounts = Array.isArray(params?.accounts) ? params.accounts : [];

                    accounts.forEach((accountId) => {
                        if (accountId) {
                            searchParams.append('accounts', accountId);
                        }
                    });

                    return searchParams.toString();
                }
            });
            return response.data;
        },
        enabled: Boolean(token) && selectedAccounts.length > 0,
        onError: (error) => {
            if (error.response?.status === 404) {
                return [];
            }
            throw error;
        },
        initialData: []
    });

    const uniqueSymbols = useMemo(() => [...new Set(allSymbolsTraded)].sort(), [allSymbolsTraded]);

    return { allSymbolsTraded, uniqueSymbols, isFetched };
};

/**
 * Fetch trading plans for tradebook filtering
 */
export const useTradingPlans = () => {
    const { pathname } = useLocation();
    const { allAccounts } = useAccountStore((state) => state);
    const { filter } = useSettingStore((state) => state);
    const { getUser } = useUserState();
    const { viewAs } = useManagementStore((state) => state);
    const user = getUser();
    const isUnassignedRoute = pathname.startsWith('/admin/u/');
    const filteredAccounts = Array.isArray(filter?.accounts) ? [...filter.accounts].sort() : [];
    const fallbackUnassignedAccounts = isUnassignedRoute
        ? allAccounts
              .map((account) => account?.key)
              .filter(Boolean)
              .sort()
        : [];
    const selectedAccounts =
        filteredAccounts.length > 0 ? filteredAccounts : fallbackUnassignedAccounts;
    const accountScopeKey = selectedAccounts.join(',');

    const { data: tradingPlans = [], isFetched: hasFetchedTradingPlans } = useQuery({
        queryKey: ['tradingPlans', viewAs],
        queryFn: () => TRADEBOOK.fetchTradingPlans(),
        enabled: !!user?.token,
        staleTime: 5 * 60 * 1000
    });

    const { data: availableTradingPlanIds = [], isFetched: hasFetchedAccountTradingPlans } =
        useQuery({
            queryKey: ['accountTradingPlans', accountScopeKey],
            queryFn: async () => {
                const response = await axios.get('crud/trades/filter', {
                    params: {
                        accounts: selectedAccounts,
                        startDate: dayjs().add(-20, 'y').startOf('day').toDate(),
                        endDate: dayjs().add(20, 'y').endOf('day').toDate(),
                        getAllofTickers: true
                    },
                    paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'brackets' })
                });

                return [
                    ...new Set(
                        (Array.isArray(response?.data) ? response.data : [])
                            .map((trade) => {
                                const tradingPlanRef = trade?.tradingPlanRef;

                                if (!tradingPlanRef) return null;
                                if (typeof tradingPlanRef === 'string') return tradingPlanRef;

                                return tradingPlanRef?._id || null;
                            })
                            .filter(Boolean)
                    )
                ];
            },
            enabled: selectedAccounts.length > 0,
            initialData: [],
            staleTime: 5 * 60 * 1000
        });

    const visibleTradingPlans = useMemo(() => {
        if (!hasFetchedAccountTradingPlans) return [];
        if (availableTradingPlanIds.length === 0) return [];

        const availablePlanIdSet = new Set(availableTradingPlanIds);

        return tradingPlans.filter((plan) => availablePlanIdSet.has(plan._id));
    }, [availableTradingPlanIds, hasFetchedAccountTradingPlans, tradingPlans]);

    const tradingPlanOptions = useMemo(
        () => [
            { label: 'All Plans', value: null },
            ...visibleTradingPlans.map((plan) => ({
                label: plan.strategyName,
                value: plan._id
            }))
        ],
        [visibleTradingPlans]
    );

    return {
        tradingPlans: visibleTradingPlans,
        tradingPlanOptions,
        hasFetchedTradingPlans:
            hasFetchedTradingPlans &&
            (selectedAccounts.length === 0 || hasFetchedAccountTradingPlans)
    };
};

/**
 * Fetch available strategy sets for a trading plan
 */
export const useStrategySets = (tradingPlanId, strategySetType) => {
    const { data: availableSets = { strategy: [], noStrategy: [] } } = useQuery({
        queryKey: ['strategySets', tradingPlanId, strategySetType],
        queryFn: () => TRADEBOOK.fetchAvailableStrategySets(tradingPlanId, strategySetType),
        enabled: !!tradingPlanId,
        staleTime: 2 * 60 * 1000
    });

    const setNumberOptions = useMemo(() => {
        const options = [{ label: 'All Set Numbers', value: null }];

        if (!tradingPlanId) return options;

        let numbers = [];
        if (strategySetType === 'strategy') {
            numbers = availableSets.strategy || [];
        } else if (strategySetType === 'noStrategy') {
            numbers = availableSets.noStrategy || [];
        } else {
            numbers = [
                ...new Set([...(availableSets.strategy || []), ...(availableSets.noStrategy || [])])
            ].sort((a, b) => a - b);
        }

        numbers.forEach((num) => {
            options.push({ label: `Set #${num}`, value: num });
        });

        return options;
    }, [tradingPlanId, strategySetType, availableSets]);

    return { availableSets, setNumberOptions };
};
